import { useState, useEffect, useMemo, useCallback, useRef, type ChangeEvent, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Trash2,
  Download,
  ChevronRight,
  ChevronLeft,
  RotateCcw,
  Info,
} from 'lucide-react';
import SearchableSelect, { type SearchableSelectOption } from './SearchableSelect';
import { getAllAccounts } from '../utils/accountApi';
import { bulkSetStartingValues } from '../utils/inventoryStartingValueApi';
import { ApiValidationError } from '../types/auth';
import type { GlAccount } from '../types/account';
import { formatCurrency } from '../utils/formatCurrency';
import { btnPrimary, btnSecondary } from '../utils/uiTheme';
import { toast } from '../utils/toast';
import {
  BULK_OPENING_STOCK_BATCH_SIZE,
  countOpeningStockRows,
  chunkOpeningStockRows,
  exportRowsToCsv,
  lineTotalForRow,
  OPENING_STOCK_CSV_TEMPLATE,
  parseOpeningStockCsv,
  rowsToApiLines,
  totalValueForRows,
  validateOpeningStockRows,
  type OpeningStockImportRow,
} from '../utils/bulkOpeningStockImport';
import {
  defaultEquityAccountId,
  todayIso,
} from '../utils/inventoryStartingValueForm';

type WizardStep = 1 | 2 | 3 | 4;

type BatchStatus = 'pending' | 'processing' | 'success' | 'failed';

interface BatchResult {
  index: number;
  rows: OpeningStockImportRow[];
  status: BatchStatus;
  error?: string;
  validationErrors?: Record<string, string[]>;
  count?: number;
  totalValue?: string;
}

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 1, label: 'Upload' },
  { id: 2, label: 'Preview' },
  { id: 3, label: 'Import' },
  { id: 4, label: 'Summary' },
];

const statusBadge = (status: OpeningStockImportRow['status']) => {
  switch (status) {
    case 'valid':
      return 'bg-green-100 text-green-800';
    case 'invalid':
      return 'bg-red-100 text-red-800';
    case 'skipped':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const downloadTemplate = () => {
  const blob = new Blob([OPENING_STOCK_CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'opening-stock-template.csv';
  link.click();
  URL.revokeObjectURL(url);
};

const BulkOpeningStockImport = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importAbortRef = useRef(false);
  const batchResultsRef = useRef<BatchResult[]>([]);

  const [step, setStep] = useState<WizardStep>(1);
  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);

  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [rows, setRows] = useState<OpeningStockImportRow[]>([]);

  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [equityAccountId, setEquityAccountId] = useState('');
  const [batchMemo, setBatchMemo] = useState('');
  const [importValidOnly, setImportValidOnly] = useState(true);

  const [continueOnFailure, setContinueOnFailure] = useState(false);
  const [batchResults, setBatchResults] = useState<BatchResult[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importFinished, setImportFinished] = useState(false);

  const equityAccounts = useMemo(
    () => accounts.filter((a) => a.active && a.account_type === 'EQUITY'),
    [accounts]
  );

  const equityOptions: SearchableSelectOption[] = useMemo(
    () =>
      equityAccounts.map((account) => ({
        value: account.id,
        label: `${account.account_number} — ${account.name}`,
        description: account.account_type,
      })),
    [equityAccounts]
  );

  const validatedRows = useMemo(() => validateOpeningStockRows(rows), [rows]);
  const counts = useMemo(() => countOpeningStockRows(validatedRows), [validatedRows]);
  const previewTotal = useMemo(() => totalValueForRows(validatedRows), [validatedRows]);
  const batches = useMemo(() => chunkOpeningStockRows(validatedRows), [validatedRows]);

  const importedStats = useMemo(() => {
    let importedCount = 0;
    let importedValue = 0;
    let failedCount = 0;

    batchResults.forEach((batch) => {
      if (batch.status === 'success') {
        importedCount += batch.count ?? batch.rows.length;
        importedValue += Number(batch.totalValue ?? 0);
      } else if (batch.status === 'failed') {
        failedCount += batch.rows.length;
      }
    });

    return { importedCount, importedValue, failedCount };
  }, [batchResults]);

  batchResultsRef.current = batchResults;

  const failedImportRows = useMemo(
    () => batchResults.filter((b) => b.status === 'failed').flatMap((b) => b.rows),
    [batchResults]
  );

  const exportableFailedRows = useMemo(() => {
    const invalidRows = validatedRows.filter((row) => !row.removed && row.status === 'invalid');
    const apiFailed = failedImportRows.map((row) => ({
      ...row,
      status: 'invalid' as const,
      errors: ['Import batch failed'],
    }));
    return [...invalidRows, ...apiFailed];
  }, [validatedRows, failedImportRows]);

  useEffect(() => {
    const load = async () => {
      try {
        const accountList = await getAllAccounts();
        setAccounts(accountList);
        setEquityAccountId(defaultEquityAccountId(accountList));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load accounts');
      } finally {
        setAccountsLoading(false);
      }
    };
    void load();
  }, []);

  const applyRows = useCallback((parsed: OpeningStockImportRow[], sourceName: string | null) => {
    setRows(parsed);
    setFileName(sourceName);
    setParseError(parsed.length === 0 ? 'No data rows found in the file.' : null);
  }, []);

  const readCsvFile = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setParseError('Please upload a .csv file.');
        return;
      }

      try {
        const text = await file.text();
        const parsed = parseOpeningStockCsv(text);
        applyRows(parsed, file.name);
      } catch {
        setParseError('Could not read the CSV file.');
        setRows([]);
        setFileName(null);
      }
    },
    [applyRows]
  );

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void readCsvFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void readCsvFile(file);
  };

  const updateRow = (id: string, patch: Partial<OpeningStockImportRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, removed: true } : row)));
  };

  const canProceedToPreview = rows.length > 0 && !parseError && asOfDate.trim() !== '';
  const canStartImport =
    counts.valid > 0 && (counts.invalid === 0 || importValidOnly) && batches.length > 0;

  const buildBatchMemo = (batchIndex: number, totalBatches: number) => {
    const base = batchMemo.trim();
    const suffix = `Opening stock batch ${batchIndex + 1} of ${totalBatches}`;
    return base ? `${base} — ${suffix}` : suffix;
  };

  const runImport = async (startFromIndex = 0) => {
    if (batches.length === 0) return;

    importAbortRef.current = false;
    setImporting(true);
    setImportFinished(false);
    setStep(3);

    const equityId = equityAccountId ? Number(equityAccountId) : undefined;
    const previousResults = batchResultsRef.current;

    const initialResults: BatchResult[] = batches.map((batchRows, index) => {
      const previous = previousResults[index];
      if (startFromIndex > 0 && index < startFromIndex && previous?.status === 'success') {
        return previous;
      }
      return {
        index,
        rows: batchRows,
        status: 'pending' as BatchStatus,
      };
    });

    setBatchResults(initialResults);

    for (let i = startFromIndex; i < batches.length; i += 1) {
      if (importAbortRef.current) break;

      const batchRows = batches[i];
      setCurrentBatchIndex(i);
      setBatchResults((prev) =>
        prev.map((batch) => (batch.index === i ? { ...batch, status: 'processing' } : batch))
      );

      try {
        const result = await bulkSetStartingValues({
          as_of_date: asOfDate,
          ...(equityId ? { equity_account_id: equityId } : {}),
          memo: buildBatchMemo(i, batches.length),
          lines: rowsToApiLines(batchRows),
        });

        setBatchResults((prev) =>
          prev.map((batch) =>
            batch.index === i
              ? {
                  ...batch,
                  status: 'success',
                  count: result.meta.count,
                  totalValue: result.meta.total_value,
                  error: undefined,
                  validationErrors: undefined,
                }
              : batch
          )
        );
      } catch (err) {
        const message =
          err instanceof ApiValidationError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Batch import failed';

        setBatchResults((prev) =>
          prev.map((batch) =>
            batch.index === i
              ? {
                  ...batch,
                  status: 'failed',
                  error: message,
                  validationErrors:
                    err instanceof ApiValidationError ? err.errors : undefined,
                }
              : batch
          )
        );

        if (!continueOnFailure) {
          break;
        }
      }
    }

    setImporting(false);
    setImportFinished(true);
    setStep(4);
  };

  const retryFailedBatch = (index: number) => {
    void runImport(index);
  };

  const renderStepIndicator = () => (
    <ol className="flex flex-wrap items-center gap-2 mb-8">
      {STEPS.map((s, idx) => {
        const isActive = step === s.id;
        const isComplete = step > s.id;
        return (
          <li key={s.id} className="flex items-center gap-2">
            <span
              className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-semibold ${
                isActive
                  ? 'bg-slate-800 text-white'
                  : isComplete
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-500'
              }`}
            >
              {isComplete ? <CheckCircle2 className="h-4 w-4" /> : s.id}
            </span>
            <span className={`text-sm ${isActive ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
              {s.label}
            </span>
            {idx < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-gray-300 mx-1" />}
          </li>
        );
      })}
    </ol>
  );

  const renderUploadStep = () => (
    <div className="space-y-8">
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8">
        <h2 className="text-base font-semibold text-gray-900 mb-5">Upload CSV</h2>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-slate-400 transition-colors bg-gray-50/50"
        >
          <Upload className="h-10 w-10 text-gray-400 mx-auto mb-4" />
          <p className="text-sm text-gray-700 mb-1">Drag and drop your CSV here, or</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`${btnSecondary} mt-2`}
          >
            Choose file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileChange}
          />
          <p className="text-xs text-gray-500 mt-4">
            Columns: <span className="font-mono">sku</span>, <span className="font-mono">qty</span>,{' '}
            <span className="font-mono">unit_cost</span>, optional <span className="font-mono">memo</span>
          </p>
          <button
            type="button"
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 text-xs text-slate-700 hover:text-slate-900 mt-3 underline"
          >
            <Download className="h-3.5 w-3.5" />
            Download template
          </button>
        </div>

        {fileName && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-700">
            <FileSpreadsheet className="h-4 w-4 text-green-600" />
            <span className="font-medium">{fileName}</span>
            <span className="text-gray-400">·</span>
            <span>{rows.length} row{rows.length === 1 ? '' : 's'}</span>
            {counts.valid > 0 && (
              <>
                <span className="text-gray-400">·</span>
                <span className="font-semibold text-slate-800">{formatCurrency(previewTotal)}</span>
              </>
            )}
          </div>
        )}

        {parseError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            {parseError}
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8">
        <h2 className="text-base font-semibold text-gray-900 mb-5">Opening details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              As-of date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Batch memo</label>
            <input
              type="text"
              value={batchMemo}
              onChange={(e) => setBatchMemo(e.target.value)}
              placeholder="Optional — applied to each batch"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 mb-1.5">
              Equity account
              <span title="Credit side of the opening entry (defaults to Owner Equity 3000)">
                <Info className="h-3.5 w-3.5 text-gray-400" />
              </span>
            </label>
            <SearchableSelect
              options={equityOptions}
              value={equityAccountId ? Number(equityAccountId) : 0}
              onChange={(value) => setEquityAccountId(value ? String(value) : '')}
              placeholder="Default: Owner Equity (3000)"
              searchPlaceholder="Search equity accounts..."
              clearValue={0}
              disabled={accountsLoading}
              isLoading={accountsLoading}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Each batch posts Dr Inventory Asset / Cr Owner Equity for up to{' '}
          {BULK_OPENING_STOCK_BATCH_SIZE} SKUs.
        </p>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!canProceedToPreview}
          onClick={() => setStep(2)}
          className={`${btnPrimary} gap-2`}
        >
          Preview & validate
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 font-medium">
          {counts.valid} valid
        </span>
        <span className="px-3 py-1 rounded-full bg-red-100 text-red-800 font-medium">
          {counts.invalid} invalid
        </span>
        <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
          {counts.skipped} skipped
        </span>
        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-800 font-medium">
          {batches.length} batch{batches.length === 1 ? '' : 'es'} · {formatCurrency(previewTotal)}
        </span>
      </div>

      {counts.invalid > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex flex-wrap items-center justify-between gap-3">
          <span>{counts.invalid} row(s) have validation errors.</span>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importValidOnly}
              onChange={(e) => setImportValidOnly(e.target.checked)}
              className="rounded border-gray-300 text-slate-800 focus:ring-slate-500"
            />
            Import valid rows only
          </label>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[min(60vh,520px)]">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">SKU</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Qty</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Unit cost</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Line total</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Error(s)</th>
                <th className="px-3 py-2 w-10" aria-hidden="true" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {validatedRows
                .filter((row) => !row.removed)
                .map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/80 align-top">
                    <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.sku}
                        onChange={(e) => updateRow(row.id, { sku: e.target.value })}
                        className="w-full min-w-[120px] px-2 py-1 border border-gray-300 rounded font-mono text-xs focus:ring-2 focus:ring-slate-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.qty}
                        onChange={(e) => updateRow(row.id, { qty: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded font-mono text-xs text-right focus:ring-2 focus:ring-slate-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.unit_cost}
                        onChange={(e) => updateRow(row.id, { unit_cost: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded font-mono text-xs text-right focus:ring-2 focus:ring-slate-500"
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-gray-700">
                      {lineTotalForRow(row) > 0 ? formatCurrency(lineTotalForRow(row)) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${statusBadge(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-red-700 max-w-[200px]">
                      {row.errors.length > 0 ? row.errors.join('; ') : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Remove row"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={() => setStep(1)} className={`${btnSecondary} gap-2`}>
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={continueOnFailure}
              onChange={(e) => setContinueOnFailure(e.target.checked)}
              className="rounded border-gray-300 text-slate-800 focus:ring-slate-500"
            />
            Continue on batch failure
          </label>
          <button
            type="button"
            disabled={!canStartImport || importing}
            onClick={() => void runImport(0)}
            className={`${btnPrimary} gap-2`}
          >
            Import {counts.valid} item{counts.valid === 1 ? '' : 's'} in {batches.length} batch
            {batches.length === 1 ? '' : 'es'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderImportStep = () => {
    const totalBatches = batches.length;
    const processingBatch = batchResults.find((b) => b.status === 'processing');

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            {importing ? (
              <Loader2 className="h-6 w-6 text-slate-800 animate-spin" />
            ) : importFinished ? (
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            ) : (
              <Loader2 className="h-6 w-6 text-gray-400" />
            )}
            <div>
              <p className="font-semibold text-gray-900">
                {importing && processingBatch
                  ? `Batch ${currentBatchIndex + 1} of ${totalBatches} — ${processingBatch.rows.length} items — Processing…`
                  : importFinished
                    ? 'Import complete'
                    : 'Preparing import…'}
              </p>
              <p className="text-sm text-gray-500">
                Imported {importedStats.importedCount} ·{' '}
                {formatCurrency(importedStats.importedValue)} · Failed {importedStats.failedCount}
              </p>
            </div>
          </div>

          <div className="w-full bg-gray-100 rounded-full h-2 mb-6">
            <div
              className="bg-slate-800 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${totalBatches > 0 ? ((batchResults.filter((b) => b.status === 'success' || b.status === 'failed').length / totalBatches) * 100) : 0}%`,
              }}
            />
          </div>

          <div className="space-y-3 max-h-[min(50vh,400px)] overflow-y-auto">
            {batchResults.map((batch) => (
              <div
                key={batch.index}
                className={`p-4 rounded-lg border ${
                  batch.status === 'success'
                    ? 'border-green-200 bg-green-50'
                    : batch.status === 'failed'
                      ? 'border-red-200 bg-red-50'
                      : batch.status === 'processing'
                        ? 'border-slate-300 bg-slate-50'
                        : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">
                      Batch {batch.index + 1} of {totalBatches} — {batch.rows.length} items
                    </p>
                    {batch.status === 'success' && (
                      <p className="text-sm text-green-800 mt-1">
                        {batch.count ?? batch.rows.length} imported ·{' '}
                        {formatCurrency(Number(batch.totalValue ?? 0))}
                      </p>
                    )}
                    {batch.status === 'failed' && (
                      <p className="text-sm text-red-800 mt-1">{batch.error}</p>
                    )}
                    {batch.validationErrors && (
                      <ul className="text-xs text-red-700 mt-2 list-disc list-inside">
                        {Object.entries(batch.validationErrors)
                          .slice(0, 5)
                          .map(([field, messages]) => (
                            <li key={field}>
                              {field}: {messages.join(', ')}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {batch.status === 'success' && (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    )}
                    {batch.status === 'failed' && (
                      <>
                        <XCircle className="h-5 w-5 text-red-600" />
                        <button
                          type="button"
                          disabled={importing}
                          onClick={() => retryFailedBatch(batch.index)}
                          className={`${btnSecondary} text-xs py-1.5 px-3 gap-1`}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Retry
                        </button>
                      </>
                    )}
                    {batch.status === 'processing' && (
                      <Loader2 className="h-5 w-5 text-slate-700 animate-spin" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {importFinished && (
          <div className="flex justify-end">
            <button type="button" onClick={() => setStep(4)} className={btnPrimary}>
              View summary
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderSummaryStep = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Import summary</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-green-50 border border-green-100">
            <dt className="text-sm text-green-800">Imported</dt>
            <dd className="text-2xl font-bold text-green-900 mt-1">{importedStats.importedCount}</dd>
          </div>
          <div className="p-4 rounded-lg bg-red-50 border border-red-100">
            <dt className="text-sm text-red-800">Failed (API)</dt>
            <dd className="text-2xl font-bold text-red-900 mt-1">{importedStats.failedCount}</dd>
          </div>
          <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
            <dt className="text-sm text-gray-600">Skipped (qty = 0)</dt>
            <dd className="text-2xl font-bold text-gray-900 mt-1">{counts.skipped}</dd>
          </div>
          <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
            <dt className="text-sm text-slate-700">Grand total value</dt>
            <dd className="text-2xl font-bold text-slate-900 mt-1 font-mono">
              {formatCurrency(importedStats.importedValue)}
            </dd>
          </div>
        </dl>

        {counts.invalid > 0 && (
          <p className="text-sm text-amber-800 mt-4">
            {counts.invalid} row(s) were invalid and were not sent to the API.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Link to="/inventory" className={btnSecondary}>
          Back to inventory
        </Link>
        <Link
          to="/inventory-transactions"
          state={{ typeFilter: 'starting_value' }}
          className={btnSecondary}
        >
          View opening stock transactions
        </Link>
        <Link to="/inventory?tab=stock-valuation" className={btnSecondary}>
          View stock valuation report
        </Link>
        {exportableFailedRows.length > 0 && (
          <button
            type="button"
            onClick={() =>
              exportRowsToCsv(exportableFailedRows, `opening-stock-failed-${todayIso()}.csv`)
            }
            className={`${btnSecondary} gap-2`}
          >
            <Download className="h-4 w-4" />
            Export failed rows CSV
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setStep(1);
            setRows([]);
            setFileName(null);
            setParseError(null);
            setBatchResults([]);
            setImportFinished(false);
            setCurrentBatchIndex(0);
          }}
          className={btnPrimary}
        >
          Start new import
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Bulk Opening Stock Import</h1>
            <p className="text-sm text-gray-600 mt-1">
              Upload a CSV to set opening quantity and FIFO unit cost — one-time go-live migration.
              Imports run in batches of {BULK_OPENING_STOCK_BATCH_SIZE}.
            </p>
          </div>

          {renderStepIndicator()}

          {step === 1 && renderUploadStep()}
          {step === 2 && renderPreviewStep()}
          {step === 3 && renderImportStep()}
          {step === 4 && renderSummaryStep()}
        </div>
      </div>
    </div>
  );
};

export default BulkOpeningStockImport;
